import React from "react";
import { SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { OverviewPanel } from "./OverviewPanel";
import { OverviewSites } from "../Types";
import { ServiceHealth, ServiceScope, SiteSummary, worseHealth } from "./overviewModel";
import { SitesMap } from "./SitesMap";
import { SiteSelection, SitesDiagram } from "./SitesDiagram";

function hasMapData(locations: OverviewSites | null): boolean {
  return (locations?.sites ?? []).some((location) => location.lat !== null && location.lng !== null);
}

function siteHealth(site: SiteSummary): ServiceHealth {
  return site.portsDown > 0 ? "down" : "up";
}

/**
 * The tenant's topology — sites, DCIs, clouds. On the map when any site has a location on file; as a diagram
 * of the same parts when none has, so a tenant without GPS still sees how its
 * sites connect.
 */
export function SitesPanel({
  sites,
  locations,
  scope,
  serviceHealthBySite,
  cloudHealthBySite,
  selectedNodeKey,
  onSelectNode,
  toolbar,
  isLoading,
}: {
  sites: SiteSummary[];
  scope: ServiceScope;
  /** Worst state of the services in `scope` at each site (serviceHealthBySite). */
  serviceHealthBySite: Record<string, ServiceHealth>;
  /** Worst state of the cloud services at each site, for the cloud lines. */
  cloudHealthBySite: Record<string, ServiceHealth>;
  selectedNodeKey: string | null;
  onSelectNode: (site: SiteSelection | null) => void;
  /** The scope chips, in the header so they cost no row of their own. */
  toolbar: React.ReactNode;
  /** Names, coordinates and DCI links; null when the page reads the four endpoints. */
  locations: OverviewSites | null;
  isLoading: boolean;
}) {
  return (
    <OverviewPanel
      title="Topology"
      action={toolbar}
    >
      <SitesBody
        sites={sites}
        locations={locations}
        scope={scope}
        serviceHealthBySite={serviceHealthBySite}
        cloudHealthBySite={cloudHealthBySite}
        selectedNodeKey={selectedNodeKey}
        onSelectNode={onSelectNode}
        isLoading={isLoading}
      />
    </OverviewPanel>
  );
}

function SitesBody({
  sites,
  locations,
  scope,
  serviceHealthBySite,
  cloudHealthBySite,
  selectedNodeKey,
  onSelectNode,
  isLoading,
}: {
  sites: SiteSummary[];
  locations: OverviewSites | null;
  scope: ServiceScope;
  serviceHealthBySite: Record<string, ServiceHealth>;
  cloudHealthBySite: Record<string, ServiceHealth>;
  selectedNodeKey: string | null;
  onSelectNode: (site: SiteSelection | null) => void;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div aria-busy="true">
        <SkeletonBlock h={260} roundedClass="rounded-lg" decorative />
      </div>
    );
  }
  if (sites.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-semantic-text-secondary">No ports provisioned yet.</p>
    );
  }
  // A site shows the worst of what is selected: its services, plus its ports'
  // own state under "All services" and "Ports".
  const countsPorts = scope === "all" || scope === "ports";
  const healthBySite = Object.fromEntries(
    sites.map((site) => {
      const services = serviceHealthBySite[site.id] ?? "unknown";
      const health = countsPorts ? worseHealth(siteHealth(site), services) : services;
      return [site.id, health === "unknown" ? "up" : health];
    })
  );
  if (!hasMapData(locations) || !locations) {
    return (
      <SitesDiagram
        sites={sites}
        locations={locations}
        healthBySite={healthBySite}
        cloudHealthBySite={cloudHealthBySite}
        scope={scope}
        selectedKey={selectedNodeKey}
        onSelectNode={onSelectNode}
      />
    );
  }
  const unplaced = locations.sites.filter((location) => location.lat === null || location.lng === null);
  return (
    <div className="space-y-2">
      <SitesMap
        locations={locations}
        healthBySite={healthBySite}
        cloudHealthBySite={cloudHealthBySite}
        scope={scope}
        selectedKey={selectedNodeKey}
        onSelectNode={onSelectNode}
      />
      {unplaced.length > 0 && (
        <p className="text-xs text-semantic-text-secondary">
          Not on the map (no location on file):{" "}
          <span className="text-semantic-text-primary">
            {unplaced.map((location) => location.displayName).join(", ")}
          </span>
        </p>
      )}
    </div>
  );
}
